<template>
    <v-card class="layout fill-height column">
        <uv-data-table class="uv-flex-fill-remaining uv-data-table-header-style"
        :items="campaigns.items"
        :headers="headers"
        :loading="campaigns.loading"
        fixed-header hide-default-header
        :items-per-page="100"
        disable-sort
        >
            <template #header>
                <thead class="v-data-table-header">
                    <tr>
                        <template v-for="group in Object.keys(groupedHeaders)">
                            <template v-if="group==='undefined'">
                                <th class="v-data-table__divider" rowspan="2"  v-for="header in groupedHeaders[group]" :style="headerStyle(header)" :key="`h-${header.value}`">
                                    {{ header.text }}
                                </th>
                            </template>
                            <th v-else class="v-data-table__divider" :colspan="groupedHeaders[group].length" :key="group">
                                {{ $t(group) }} 
                            </th>
                        </template>
                        <th rowspan="2"></th>
                    </tr>
                    <tr>
                        <template v-for="group in Object.keys(groupedHeaders)">
                            <template v-if="group!=='undefined'">
                                <th v-for="header in groupedHeaders[group]" class="v-data-table__divider" :key="`h-${header.value}`">
                                    {{ header.text }}
                                </th>
                            </template>
                        </template>
                    </tr>
                </thead>
            </template>
            <template #item="{item, index, headers}">
                <tr>
                    <td v-for="header in headers" :key="`td-${index}-${header.value}`" :style="{'text-align': header.align ?? 'left', ...cellStyle(header)}"
                    >
                        <template v-if="header.value=='active'">
                            <v-tooltip top max-width="300px" :open-on-focus="false" >
                                <template #activator="{ on, attrs }">
                                    <v-icon v-on="on" v-bind="attrs" :color="item.active ? 'green' : 'red'">{{ icons?.mdiCircle }}</v-icon>
                                </template>
                                <span>{{ item.active ? $t("active") : $t("expired") }}</span>
                            </v-tooltip>
                        </template>
                        <template v-else-if="header.value=='date_start' || header.value=='date_stop'">
                            {{ dateFormat(item[header.value]) }}
                        </template>
                        <template v-else-if="header.cellClass">
                            <v-tooltip top max-width="300px" :open-on-focus="false" >
                                <template #activator="{ on, attrs }">
                                    <div v-on="on" v-bind="attrs" :style="cellStyle(header)" :class="header.cellClass">{{ getItemValue(item, header.value) }}</div>
                                </template>
                                <span>{{ getItemValue(item, header.value) }}</span>
                            </v-tooltip>
                        </template>
                        <template v-else>{{ getItemValue(item, header.value) }}</template>
                    </td>
                    <td>
                        <v-layout style="gap:5px">
                            <uv-button small :title="$t('labels.edit')" icon color='warning' @click="edit(item)">
                                <v-icon>$edit</v-icon>
                            </uv-button>
                            <uv-button small :title="$t('delete')" :loading="item.deleting" icon color='error' @click="del(item)">
                                <v-icon>$delete</v-icon>
                            </uv-button>
                        </v-layout>
                    </td>
                </tr>
            </template>
            <template #actions>
                <uv-button small color="success"  @click="edit({brand_id:brandId})" :title="$t('labels.add')">
                    <v-icon >$plus</v-icon>
                </uv-button>
            </template>
        </uv-data-table>
        <v-dialog v-if="selected != null" :value="true" persistent>
            <campaign-form :campaign="selected" @close="()=>selected=null" @saved="handleSave"/>
        </v-dialog>
        <uv-confirm-dialog ref="confirmDialog">
            {{ $t('messages.deleteCampaign', {name:toDelete}) }}
        </uv-confirm-dialog>
    </v-card>
</template>

<script>
import {reactive} from 'vue'
import {numFormatMixin} from '@/plugins/mixins'
import CampaignForm from './CampaignForm.vue'
export default {
    name: "CampaignsList",
    mixins: [numFormatMixin],
    components: {
        CampaignForm
    },
    created(){
        this.getCampaigns()
    },
    setup(){
        const icons = reactive({})
        import('@mdi/js').then((mdi) => {
            icons.mdiCircle = mdi.mdiCircle
        })

        return {icons}
    },
    data(){
        return {
            campaigns: {
                items: [],
                loading: false,
            },
            selected: null,
            toDelete: "",
        }
    },
    computed: {
        brandId(){
            return this.$route.params.brandId
        },
        headers(){
            return [
                {
                    text: this.$t("labels.name"), 
                    value: "name",
                    width: '10em',
                    cellClass: 'ellipsis',
                    divider: true
                },
                {
                    text: this.$t("labels.description"),
                    value: "description",
                    width: '10em',
                    cellClass: 'ellipsis'
                },
                {
                    text: this.$t('labels.brand'),
                    value: 'brand.display_name',
                },
                {
                    text: this.$t('labels.carModel'),
                    value: 'model.display_name',
                },
                {
                    text: 'GMUD',
                    value: 'submodel.gmud',
                },
                {
                    text: this.$t('labels.status'),
                    value: 'active',
                    width: '100px',
                    align: 'center'
                },
                {
                    text: this.$t('labels.dateStart'),
                    value: 'date_start',
                    // width: '120px',
                    align: 'center'
                },
                {
                    text: this.$t('labels.dateStop'),
                    value: 'date_stop',
                    // width: '100px',
                    align: 'center'
                },
                {
                    text: "%",
                    value: 'importer_discount_percentage',
                    group: 'labels.suportImportator',
                    align: 'right'
                },
                {
                    text: this.$t('labels.value'),
                    value: 'importer_discount',
                    group: 'labels.suportImportator',
                    align: 'right'
                },
                {
                    text: "%",
                    value: 'dealer_discount_percentage',
                    group: 'labels.dealerDiscount',
                    align: 'right'
                },
                {
                    text: this.$t('labels.value'),
                    value: 'dealer_discount',
                    group: 'labels.dealerDiscount',
                    align: 'right'
                },
                {
                    text: this.$t('labels.value'),
                    value: 'credit_note',
                    group: 'labels.creditNote',
                    align: 'right'
                },
                {
                    text: "%",
                    value: 'credit_note_percentage',
                    group: 'labels.creditNote',
                    align: 'right'
                }
            ]
        },
        groupedHeaders(){
            return Object.fromEntries(Map.groupBy(this.headers, h=>h.group).entries())
        }
    },
    methods:{
        getCampaigns() {
            this.campaigns.loading = true;
            this.$apiHttp({
                url: "/campaigns",
                params: {
                    brand_id: this.brandId
                }
            })
            .then((res) => {
                this.campaigns.items = res.data.map(c=>{
                    c.deleting = false
                    return c
                })
            })
            .finally(() => {
                this.campaigns.loading = false
            })
        },
        headerStyle(header){
            return {
                'min-width': header.width ?? false,
                'max-width': header.width ?? false,
                width: header.width ?? false
            }
        },
        cellStyle(header){
            return {
                'min-width': header.width ?? false,
                'max-width': header.width ?? false,
                width: header.width ?? false,
            }
        },
        getItemValue(item, key){
            let keys = key.split('.')
            let value = item
            for (let i = 0; i < keys.length; i++){
                value = value[keys[i]]
                if (!value){
                    return ''
                }
            }
            if (typeof value === 'number'){
                return this.numFormat(value)
            }
            return value
        },
        edit(campaign){
            this.selected = campaign
        },
        del(campaign){
            this.toDelete = campaign.name
            this.$refs.confirmDialog.open().then(ok => {
                if (!ok){
                    return
                }
                campaign.deleting = true
                this.$apiHttp({
                    url: `/campaigns/${campaign.id}`,
                    method: 'delete'
                }).then(() => {
                    let index = this.campaigns.items.findIndex(c => c.id == campaign.id)
                    if (index == -1){
                        return
                    }
                    this.campaigns.items.splice(index, 1)
                }).finally(() => {
                    campaign.deleting = false
                })
            })
        },
        handleSave(campaign){
            let index = this.campaigns.items.findIndex(c => c.id == campaign.id)
            if (index != -1){
                this.campaigns.items.splice(index, 1, campaign)
            }else{
                this.campaigns.items.push(campaign)
            }
            this.selected = null
        },
    },
}
</script>
<style lang="scss">
.ellipsis {
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
}
</style>